import { CustomerScanDataOverviewFragment, Cpg, TimeSeriesDataPoint } from '@occc/graphql';
import { formatMoneyAmount } from '@occc-app/lib/currency';

import { SHOW_CENTS_THRESHOLD } from './scan-data-overview.constants';
import { ScanDataMetric } from './scan-data-overview.types';

import { useScanDataMetricsTimeSeries } from '../../hooks/use-scan-data-metrics-time-series.hook';

export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '–';
  }

  return String(value);
};

const defaultStatusByCpg = {
  [Cpg.Altria]: false,
  [Cpg.Circana]: false,
  [Cpg.Msa]: false,
} as const satisfies Record<Cpg, boolean>;

export const createStatusByCpgMap = (
  cpgSubmissionStatus: CustomerScanDataOverviewFragment['cpgSubmissionStatus'] | undefined,
): Record<Cpg, boolean> => {
  if (!cpgSubmissionStatus) {
    return { ...defaultStatusByCpg };
  }

  const statusByCpg: Record<Cpg, boolean> = { ...defaultStatusByCpg };

  cpgSubmissionStatus.forEach((status) => {
    if (status?.cpg) {
      statusByCpg[status.cpg] = status.enabled;
    }
  });

  return statusByCpg;
};

export const formatMoneyAmountWithCentsThreshold = (
  amount: number,
  threshold: number = SHOW_CENTS_THRESHOLD,
): string => {
  const showCents = amount !== 0 && amount < threshold;
  return formatMoneyAmount(amount, { showCents });
};

export const createMetricsFromTimeSeriesData = (
  scanDataMetricsTimeSeries: ReturnType<typeof useScanDataMetricsTimeSeries>,
): ScanDataMetric[] => {
  const data = scanDataMetricsTimeSeries.data?.scanDataMetricsTimeSeries;

  const getLatestAndPreviousValues = (timeSeries: TimeSeriesDataPoint[]) => {
    const current = timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].value : 0;
    const previous = timeSeries.length > 1 ? timeSeries[timeSeries.length - 2].value : 0;

    return {
      current,
      previous,
    };
  };

  const storesSubmittedValues = getLatestAndPreviousValues(data?.storesSubmitted ?? []);
  const missingStoresValues = getLatestAndPreviousValues(data?.missingStores ?? []);
  const transactionsReportedValues = getLatestAndPreviousValues(data?.transactionsReported ?? []);
  const reimbursementsValues = getLatestAndPreviousValues(data?.reimbursements ?? []);

  return [
    {
      metricType: 'stores-submitted',
      label: 'Stores Submitted',
      value: storesSubmittedValues.current,
      prevValue: storesSubmittedValues.previous,
    },
    {
      metricType: 'missing-stores',
      label: 'Missing Stores',
      value: missingStoresValues.current,
      prevValue: missingStoresValues.previous,
    },
    {
      metricType: 'transactions-reported',
      label: 'Transactions Reported',
      value: transactionsReportedValues.current,
      prevValue: transactionsReportedValues.previous,
    },
    {
      metricType: 'reimbursements',
      label: 'Reimbursements',
      value: reimbursementsValues.current,
      prevValue: reimbursementsValues.previous,
      valueType: 'currency',
    },
  ];
};

export const calculateMissingStoresPercentage = (
  missingStoresCount: number,
  submittedStoresCount: number,
): number => {
  const totalStoresCount = submittedStoresCount + missingStoresCount;
  if (totalStoresCount === 0) {
    return 0;
  }
  return (missingStoresCount * 100) / totalStoresCount;
};
